// S41 AI 使用記錄 /ai-usage — 平台各 consumer 的 LLM 呼叫稽核(唯讀)。
//
// 篩選(consumer 下拉 / actor / 日期範圍)→ 摘要列表(時間/consumer/actor/model/tokens/延遲/
// ok/preview;cursor 分頁「載入更多」)→ 點列開右側抽屜看**完整 request + response** + tokens/
// 延遲 + 隱私提示(含完整對話內容、保留 N 天)。全唯讀。CanAdminRead 可見(沿用 role gate);
// 三態一律走 StateBlock。列表/明細走 restful 標準 `{data}` 信封(apiFetch)。

import { useCallback, useEffect, useRef, useState } from 'react';
import { isAdminRole, useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { CopyableId } from '../../components/CopyableId';
import { formatTimestampUs } from '../../lib/format';
import {
  fetchAiUsage,
  fetchAiUsageDetail,
  formatTokens,
  formatUsageLatency,
  type AiUsageDetail,
  type AiUsageRow,
} from './aiUsageApi';
import '../_shared/shared.css';
import './aiUsage.css';

/** CanAdminRead:所有 admin console 角色皆可檢視(伺服端仍以 requireAdmin 為準)。 */
export function canViewAiUsage(role: string | null | undefined): boolean {
  return isAdminRole(role);
}

const CONSUMER_OPTIONS = [
  { value: '', label: '全部消費端' },
  { value: 'admin', label: 'admin(管理助理)' },
  { value: 'dev-portal-assistant', label: 'dev-portal-assistant(開發者入口 · AI 助理)' },
  { value: 'dev-portal-diagnostics', label: 'dev-portal-diagnostics(開發者入口 · AI 診斷)' },
];

const PAGE = 50;

// ── 列表(純呈現;供 renderToStaticMarkup 單測欄位/preview) ─────────────────────
export function AiUsageListView({ rows, onOpen }: { rows: AiUsageRow[]; onOpen?: (id: string) => void }) {
  return (
    <div className="table-wrap" data-testid="ai-usage-table">
      <table className="data-table">
        <thead>
          <tr>
            <th>時間</th>
            <th>消費端</th>
            <th>觸發者</th>
            <th>模型</th>
            <th>用量(輸入/輸出)</th>
            <th>延遲</th>
            <th>結果</th>
            <th>內容摘要</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="clickable"
              tabIndex={0}
              onClick={() => onOpen?.(row.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onOpen?.(row.id);
              }}
            >
              <td className="adm-mono">{formatTimestampUs(row.ts)}</td>
              <td className="adm-mono">{row.consumer || '—'}</td>
              <td className="aiu-cell-actor" title={row.actor}>
                {row.actor || '—'}
              </td>
              <td className="adm-mono">{row.model || '—'}</td>
              <td className="adm-mono">{formatTokens(row.input_tokens, row.output_tokens)}</td>
              <td className="adm-mono">{formatUsageLatency(row.latency_ms)}</td>
              <td>
                <span className={`pill pill-dot ${row.ok ? 'pill-good' : 'pill-danger'}`}>{row.ok ? '成功' : '失敗'}</span>
              </td>
              <td className="aiu-cell-preview" title={row.preview}>
                {row.preview || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AiUsagePage() {
  const { role } = useAuth();

  const [consumer, setConsumer] = useState('');
  const [actorInput, setActorInput] = useState('');
  const [actor, setActor] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [rows, setRows] = useState<AiUsageRow[] | null>(null);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);

  const [openId, setOpenId] = useState<string | null>(null);

  // debounce actor 關鍵字(300ms)
  useEffect(() => {
    const t = setTimeout(() => setActor(actorInput.trim()), 300);
    return () => clearTimeout(t);
  }, [actorInput]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      setMoreError(null);
      fetchAiUsage({ consumer, actor, from, to, limit: PAGE }, signal)
        .then((page) => {
          if (signal?.aborted) return;
          setRows(page.rows);
          setCursor(page.nextCursor);
          if (page.retentionDays !== null) setRetentionDays(page.retentionDays);
        })
        .catch((e) => {
          if (!signal?.aborted) {
            setError(e);
            setRows(null);
          }
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [consumer, actor, from, to]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const loadMore = () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    fetchAiUsage({ consumer, actor, from, to, limit: PAGE, cursor })
      .then((page) => {
        setRows((prev) => [...(prev ?? []), ...page.rows]);
        setCursor(page.nextCursor);
        if (page.retentionDays !== null) setRetentionDays(page.retentionDays);
      })
      .catch((e) => setMoreError(e))
      .finally(() => setLoadingMore(false));
  };

  if (!canViewAiUsage(role)) {
    return <ErrorState error={new ApiError(403, 'FORBIDDEN', '需要更高權限才能檢視 AI 使用記錄。', null)} />;
  }

  const hasFilter = !!(consumer || actor || from || to);
  const clearFilters = () => {
    setConsumer('');
    setActorInput('');
    setFrom('');
    setTo('');
  };
  const showInitialLoading = loading && rows === null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">AI 使用記錄</h1>
          <p className="page-subtitle">
            平台各消費端(consumer)實際發生的 LLM 呼叫稽核:時間、觸發者、模型、tokens、延遲與結果。點列可下鑽<strong>完整請求與回覆內容</strong>(供品質分析與改善)。
          </p>
        </div>
        <button className="btn-ghost" onClick={() => load()} disabled={loading}>
          {loading ? '載入中…' : '重新整理'}
        </button>
      </div>

      <div className="filter-bar is-sticky" data-testid="ai-usage-filters">
        <label className="adm-field">
          <span className="text-secondary adm-field-label">消費端</span>
          <select value={consumer} onChange={(e) => setConsumer(e.target.value)} aria-label="依消費端篩選">
            {CONSUMER_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">觸發者</span>
          <input type="search" placeholder="user id(精確比對)…" aria-label="依觸發者 user id 精確比對篩選" value={actorInput} onChange={(e) => setActorInput(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">起始日期</span>
          <input type="date" aria-label="起始日期" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">結束日期</span>
          <input type="date" aria-label="結束日期" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <div className="adm-toolbar-spacer" />
        <button className="btn-ghost" disabled={!hasFilter} onClick={clearFilters}>
          清除篩選
        </button>
      </div>

      {showInitialLoading && <LoadingState label="載入 AI 使用記錄…" />}

      {error != null && rows === null && <ErrorState error={error} onRetry={() => load()} />}

      {rows && rows.length === 0 && (
        <EmptyState
          title="沒有符合條件的使用記錄"
          detail={hasFilter ? '試著放寬消費端／觸發者／日期範圍篩選。' : '目前尚無 AI 使用記錄。'}
          action={
            hasFilter ? (
              <button className="btn-ghost btn-sm" onClick={clearFilters}>
                清除篩選
              </button>
            ) : undefined
          }
        />
      )}

      {rows && rows.length > 0 && (
        <>
          <AiUsageListView rows={rows} onOpen={setOpenId} />
          <div className="adm-pager">
            <span className="text-secondary">本頁 {rows.length} 筆</span>
            {cursor && (
              <button className="btn-ghost btn-sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? '載入中…' : '載入更多'}
              </button>
            )}
            {moreError != null && (
              <span className="error-text" role="alert">
                載入更多失敗
                <button className="btn-ghost btn-sm" style={{ marginLeft: 'var(--spacing-2)' }} onClick={loadMore}>
                  重試
                </button>
              </span>
            )}
          </div>
        </>
      )}

      {openId && <AiUsageDrawer id={openId} retentionDays={retentionDays} onClose={() => setOpenId(null)} />}
    </>
  );
}

// ── 明細抽屜 ──────────────────────────────────────────────────────────────────

function AiUsageDrawer({ id, retentionDays, onClose }: { id: string; retentionDays: number | null; onClose: () => void }) {
  const [detail, setDetail] = useState<AiUsageDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    setDetail(null);
    fetchAiUsageDetail(id, ac.signal)
      .then(setDetail)
      .catch((e) => {
        if (!ac.signal.aborted) setError(e);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [id, nonce]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="aiu-drawer-scrim" role="presentation" onClick={onClose}>
      <aside className="aiu-drawer" role="dialog" aria-modal="true" aria-label="AI 使用記錄明細" onClick={(e) => e.stopPropagation()} data-testid="ai-usage-drawer">
        <div className="aiu-drawer-head">
          <h2 className="aiu-drawer-title">
            使用記錄明細
            {detail && <span className={`pill ${detail.ok ? 'pill-good' : 'pill-danger'}`}>{detail.ok ? '成功' : '失敗'}</span>}
          </h2>
          <button ref={closeRef} className="btn-ghost btn-sm aiu-drawer-close" onClick={onClose} aria-label="關閉">
            關閉
          </button>
        </div>

        {loading && <LoadingState label="載入使用記錄明細…" />}
        {error != null && <ErrorState error={error} onRetry={() => setNonce((n) => n + 1)} />}

        {detail && <AiUsageDetailView detail={detail} retentionDays={retentionDays} />}
      </aside>
    </div>
  );
}

// ── 明細本體(純呈現;供 renderToStaticMarkup 驗證完整內容 + 隱私提示) ───────────
export function AiUsageDetailView({ detail, retentionDays }: { detail: AiUsageDetail; retentionDays: number | null }) {
  const days = detail.retention_days ?? retentionDays;
  return (
    <>
      <p className="aiu-privacy" role="note">
        <span aria-hidden="true">🔒</span>
        此記錄<strong>含完整請求與回覆內容</strong>,僅供品質分析與改善;
        {days !== null ? `依保留政策 ${days} 天後自動清除。` : '依保留政策定期自動清除。'}
      </p>

      <div className="aiu-block">
        <span className="aiu-block-label">度量</span>
        <div className="aiu-meta-grid">
          <div className="aiu-meta-item">
            <span className="aiu-meta-k">記錄 ID</span>
            <span className="aiu-meta-v">
              <CopyableId value={detail.id} />
            </span>
          </div>
          <div className="aiu-meta-item">
            <span className="aiu-meta-k">消費端</span>
            <span className="aiu-meta-v adm-mono">{detail.consumer || '—'}</span>
          </div>
          <div className="aiu-meta-item">
            <span className="aiu-meta-k">觸發者</span>
            <span className="aiu-meta-v">{detail.actor || '—'}</span>
          </div>
          <div className="aiu-meta-item">
            <span className="aiu-meta-k">模型</span>
            <span className="aiu-meta-v adm-mono">{detail.model || '—'}</span>
          </div>
          <div className="aiu-meta-item">
            <span className="aiu-meta-k">用量(輸入/輸出)</span>
            <span className="aiu-meta-v adm-mono">{formatTokens(detail.input_tokens, detail.output_tokens)}</span>
          </div>
          <div className="aiu-meta-item">
            <span className="aiu-meta-k">延遲</span>
            <span className="aiu-meta-v adm-mono">{formatUsageLatency(detail.latency_ms)}</span>
          </div>
          <div className="aiu-meta-item">
            <span className="aiu-meta-k">時間</span>
            <span className="aiu-meta-v adm-mono">{formatTimestampUs(detail.ts)}</span>
          </div>
        </div>
      </div>

      <div className="aiu-block">
        <span className="aiu-block-label">完整請求</span>
        <pre className="aiu-content">{detail.request || '(空)'}</pre>
      </div>

      <div className="aiu-block">
        <span className="aiu-block-label">完整回覆</span>
        <pre className="aiu-content aiu-content--response">{detail.response || '(空)'}</pre>
      </div>
    </>
  );
}
